export type MilestoneStatus = 'not_started' | 'in_progress' | 'blocked' | 'completed';

/** Max length for a milestone note body (must match API). */
export const MILESTONE_NOTE_MAX_LENGTH = 2000;

export interface MilestoneNote {
  id: string;
  body: string;
  created_at: string;
  created_by: string;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface MilestoneNotesResponse {
  status: string;
  milestone_id: string;
  notes: MilestoneNote[];
  notes_count: number;
}

export interface MilestoneNoteMutationResponse {
  status: string;
  message: string;
  milestone_id: string;
  note: MilestoneNote;
}

export interface MilestonePathContext {
  objective_id: string;
  key_activity_id: string;
  goal_id: string;
  activity_id: string;
  milestone_id?: string;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  status: MilestoneStatus;
  due_date: string;
  completed_date?: string;
  /** When milestone status was last changed (API). */
  updated_at?: string;
  /** When the current status was set (API). */
  status_updated_at?: string;
  updated_by?: string;
  /** Friendly subawardee name when returned on component-project milestone trees. */
  subawardee?: string;
  /** Config project id when merged from component-project trees (e.g. `aswp`). */
  component_project?: string;
  /** Active (non-deleted) notes count from list/tree payloads. */
  notes_count?: number;
  activity_id: string; // API response field for activity reference
  goal_id: string; // API response field for goal reference
  key_activity_id: string; // API response field for key activity reference
  objective_id: string; // API response field for objective reference
}

export interface Activity {
  id: string;
  title: string;
  description: string;
  startDate?: string; // Optional for backward compatibility
  endDate?: string; // Optional for backward compatibility
  keyActivityId?: string; // Reference to parent key activity (optional)
  milestones: Milestone[];
}

export interface Goal {
  id: string;
  title: string;
  description: string;
  objectiveId?: string; // Reference to parent objective (optional)
  keyActivityId?: string; // Reference to parent key activity (optional)
  activities: Activity[];
}

export interface KeyActivity {
  id: string;
  title: string;
  description: string;
  goalId?: string; // Reference to parent goal (optional)
  /** Single subawardee owner when returned on component-project milestone trees. */
  subawardee?: string;
  /** Multiple subawardees when a key activity spans orgs (component-project view). */
  subawardees?: string[];
  goals: Goal[];
}

export interface Objective {
  id: string;
  title: string;
  description?: string;
  timeframe?: string;
  /** Friendly subawardee name when returned on admin/component-project trees. */
  subawardee?: string;
  /** Config project id when merged from component-project trees (e.g. `aswp`). */
  component_project?: string;
  key_activities: KeyActivity[];
}

export interface ObjectivesMetadata {
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface MilestoneData {
  objectives: Objective[];
  /** Latest update metadata for a single-org objectives response. */
  metadata?: ObjectivesMetadata | null;
  /** Per-subawardee update metadata from admin list responses. */
  metadataBySubawardee?: Record<string, ObjectivesMetadata | null>;
}

/** One component project's milestone tree — kept separate in the all-projects view. */
export interface ComponentProjectMilestoneData {
  componentProjectId: string;
  data: MilestoneData;
}

// This maintains backward compatibility with current code
export interface MilestoneGroup {
  title: string;
  milestones: Milestone[];
} 